import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronRight, Flame, Flower2, Mountain, Sparkles, HeartHandshake } from "lucide-react";
import FestivalCountdown from "../components/FestivalCountdown";
import DeityStrip from "../components/DeityStrip";
import AartiStream from "../components/AartiStream";
import AdSlot from "../components/AdSlot";
import { useLang } from "../contexts/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1609609830354-8f615d61b9c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHxfMTc5MTU3MzY5MHww&ixlib=rb-4.1.0&q=85";
const BAYON_IMG = "https://images.unsplash.com/photo-1560943701-14f678837722?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxNzkxNTczNjgzfDA&ixlib=rb-4.1.0&q=85";
const DIYA_IMG = "https://images.unsplash.com/photo-1757308530438-4e2340a6475f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxNzkxNTczNjkwfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [verse, setVerse] = useState(null);
  const [packages, setPackages] = useState([]);
  const { t } = useLang();

  useEffect(() => {
    api.get("/verses/today").then((r) => setVerse(r.data)).catch(() => {});
    api.get("/travel/packages").then((r) => setPackages(r.data.items.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A0E17]/70 via-[#4A0E17]/50 to-[#FAF6F0]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 lg:pt-28 lg:pb-44">
          <div className="max-w-3xl">
            <div className="overline text-[#D4AF37] fade-up">॥ {t("hero.subtitle")} ॥</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FAF6F0] leading-[1.05] mt-4 fade-up-delay-1">
              {t("hero.title.1")} <span className="text-[#D4AF37]">{t("hero.title.highlight")}</span> {t("hero.title.2")}
            </h1>
            <p className="mt-6 text-lg text-[#FAF6F0]/85 max-w-xl leading-relaxed fade-up-delay-2">
              {t("hero.copy")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up-delay-3">
              <Link to="/names" data-testid="hero-explore-names" className="btn-saffron">
                <Sparkles className="w-4 h-4" /> {t("hero.chant")}
              </Link>
              <Link to="/pilgrimage" data-testid="hero-book-pilgrimage" className="btn-ghost-gold !text-[#F3E5AB] !border-[#D4AF37] hover:!bg-[#D4AF37]/15">
                {t("hero.yatra")} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "1008", l: t("stat.names") },
                { n: "5 Dhams", l: t("stat.dhams") },
                { n: "365", l: t("stat.verses") },
              ].map((s) => (
                <div key={s.l} className="border-t border-[#D4AF37]/40 pt-3">
                  <div className="font-serif text-3xl text-[#F3E5AB]">{s.n}</div>
                  <div className="overline text-[#FAF6F0]/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Daily Verse */}
      {verse && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 lg:-mt-28 relative z-10">
          <div data-testid="daily-verse-card" className="bg-[#FFFDF9] sacred-border rounded-3xl p-8 md:p-12 relative overflow-hidden om-watermark">
            <div className="flex items-center gap-2 mb-5">
              <Flame className="w-4 h-4 text-[#E65100]" />
              <span className="overline">Verse of the Day · {verse.date}</span>
            </div>
            <p className="font-devanagari text-2xl md:text-3xl text-[#4A0E17] leading-loose whitespace-pre-line">{verse.sanskrit}</p>
            {verse.transliteration && (
              <p className="mt-4 italic text-[#63534E] text-base md:text-lg leading-relaxed">{verse.transliteration}</p>
            )}
            <p className="mt-6 text-[#1F1412] text-lg leading-relaxed max-w-3xl">{verse.translation}</p>
            {verse.reference && (
              <p className="mt-5 overline text-[#8C6D3B]">— {verse.reference}</p>
            )}
            <div className="mt-8 flex gap-3">
              <Link to="/daily-verse" data-testid="verse-explore-link" className="btn-primary text-sm">Explore more verses</Link>
            </div>
          </div>
        </section>
      )}

      {/* Three Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline">The Three Pillars</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#4A0E17] mt-2">Chant · Journey · Offer</h2>
          <p className="text-[#63534E] mt-4">Three sacred paths — pick one, or walk them all.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {[
            { icon: Flower2, title: "1008 Names", desc: "Browse the Shiva Sahasranama in Sanskrit, Hindi, English, with chanting companion.", to: "/names", cta: "Open the archive" },
            { icon: Mountain, title: "Cambodia Yatra", desc: "Curated multi-day pilgrimages to Pancham Dham, Angkor Wat, and the Bayon temples.", to: "/pilgrimage", cta: "View packages" },
            { icon: HeartHandshake, title: "Seva & Donations", desc: "Sponsor annadaan, abhishekam, or temple construction — in your name and gotra.", to: "/donate", cta: "Offer seva" },
          ].map((p, idx) => (
            <Link
              key={p.title}
              to={p.to}
              data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="group bg-[#FFFDF9] sacred-border rounded-2xl p-8 transition-sacred gold-glow block"
            >
              <div className="w-14 h-14 rounded-xl bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center mb-6">
                <p.icon className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl text-[#4A0E17]">{p.title}</h3>
              <p className="text-[#63534E] mt-3 leading-relaxed">{p.desc}</p>
              <div className="mt-6 flex items-center gap-1 text-[#E65100] font-semibold text-sm">
                {p.cta} <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <FestivalCountdown />
      <DeityStrip />
      <AartiStream />
      <AdSlot />

      {/* Pancham Dham About strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="overline">The Fifth Dham</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#4A0E17] mt-2 leading-tight">
            A sanctuary of Shiva, carved into the stones of Cambodia.
          </h2>
          <p className="text-[#1F1412] mt-6 leading-relaxed">
            Pancham Dham rises from the forested plains of Siem Reap — the fifth sacred abode in the lineage of Shiva's dhams. Here, the ancient Khmer devotion to Mahadeva meets the living pilgrimage tradition of Bharat.
          </p>
          <p className="text-[#63534E] mt-4 leading-relaxed">
            Visitors witness Rudrabhishekam at sunrise, chant the Sahasranama under 1000-year-old sandstone, and partake in sattvik annadaan — all preserved for every devotee, globally.
          </p>
          <Link to="/about" data-testid="about-learn-more" className="btn-primary mt-8">Read the story</Link>
        </div>
        <div className="relative">
          <img src={BAYON_IMG} alt="Bayon Temple" className="rounded-3xl w-full object-cover h-96 sacred-border" />
          <div className="absolute -bottom-8 -left-6 bg-[#FFFDF9] sacred-border rounded-2xl px-6 py-5 hidden md:block">
            <div className="overline">Siem Reap, Cambodia</div>
            <div className="font-serif text-xl text-[#4A0E17] mt-1">13.4125° N, 103.8667° E</div>
          </div>
        </div>
      </section>

      {/* Packages preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-32">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Pilgrimage Packages</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#4A0E17] mt-2">Begin your Cambodia yatra</h2>
          </div>
          <Link to="/pilgrimage" data-testid="all-packages-link" className="btn-ghost-gold text-sm">All packages →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {packages.map((p) => (
            <Link
              to={`/pilgrimage/${p.slug}`}
              data-testid={`package-card-${p.slug}`}
              key={p.id}
              className="group bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden transition-sacred gold-glow"
            >
              <div className="h-56 overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <span className="overline">{p.duration_days} days</span>
                  <span className="font-mono text-sm text-[#E65100]">${p.price_usd.toFixed(0)}</span>
                </div>
                <h3 className="font-serif text-xl text-[#4A0E17] mt-2 leading-tight">{p.title}</h3>
                <p className="text-sm text-[#63534E] mt-3 line-clamp-2">{p.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Seva banner */}
      <section className="mt-32 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${DIYA_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#4A0E17]/95 to-[#4A0E17]/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-2xl">
            <div className="overline text-[#D4AF37]">Offer a Seva</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#FAF6F0] mt-2 leading-tight">
              Light a lamp for every devotee you'll never meet.
            </h2>
            <p className="text-[#FAF6F0]/80 mt-5 leading-relaxed">
              Your annadaan feeds pilgrims. Your abhishekam carries your gotra to the altar. Choose any amount — every rupee becomes a prayer.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/donate" data-testid="seva-cta" className="btn-saffron">Donate Now</Link>
              <Link to="/newsletter" data-testid="newsletter-cta" className="btn-ghost-gold !text-[#F3E5AB] !border-[#D4AF37] hover:!bg-[#D4AF37]/15">Daily blessings in inbox</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
